import { useMemo } from 'react'

import {
  F0FormSubmitResult,
  F0WizardFormPerSectionSubmitArg,
  F0WizardFormStep,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import { TrainingRequestsCompetenciesDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  buildCommentsSchema,
  commentsDefaults,
  commentsSection,
} from 'modules/trainings/pages/MyTrainings/Requests/shared/comments'
import {
  buildEmployeesSchema,
  employeesSection,
} from 'modules/trainings/pages/MyTrainings/Requests/shared/employees'
import {
  buildTrainingNeedSchema,
  trainingNeedDefaults,
  trainingNeedSection,
} from 'modules/trainings/pages/MyTrainings/Requests/shared/trainingNeed'

import {
  basicInfoDefaults,
  basicInfoSection,
  buildBasicInfoSchema,
} from './sections/basicInfo'
import {
  buildTimingCostSchema,
  timingCostDefaults,
  timingCostSection,
} from './sections/timingCost'

const EMPTY_STRING_ARRAY: string[] = []

export interface UseNewProposalFormParams {
  allowEmployeePayment?: boolean
  showParticipantsStep: boolean
  defaultEmployeeId?: string
  currency: string
  onSubmit: (
    arg: F0WizardFormPerSectionSubmitArg<NewProposalSchema>,
  ) => Promise<F0FormSubmitResult>
}

// -- Exported types --

function buildNewProposalSchema(params: {
  allowEmployeePayment?: boolean
  canUseRichTextEditor: boolean
  richTextEditorLoading: boolean
  editorConfig: ReturnType<typeof useRichTextEditor>['editorConfig']
  competencyOptions: Array<{ value: string; label: string }>
  currency: string
}) {
  return {
    trainingNeed: buildTrainingNeedSchema({
      canUseRichTextEditor: params.canUseRichTextEditor,
      richTextEditorLoading: params.richTextEditorLoading,
      editorConfig: params.editorConfig,
    }),
    basicInfo: buildBasicInfoSchema(params.competencyOptions),
    timingCost: buildTimingCostSchema({
      allowEmployeePayment: params.allowEmployeePayment,
      currency: params.currency,
    }),
    employees: buildEmployeesSchema(),
    comments: buildCommentsSchema({
      canUseRichTextEditor: params.canUseRichTextEditor,
      richTextEditorLoading: params.richTextEditorLoading,
      editorConfig: params.editorConfig,
    }),
  }
}

export type NewProposalSchema = ReturnType<typeof buildNewProposalSchema>

export type NewProposalFormValues = {
  [K in keyof NewProposalSchema]: z.infer<NewProposalSchema[K]>
}

// -- Hook --

export const useNewProposalForm = ({
  allowEmployeePayment,
  showParticipantsStep,
  defaultEmployeeId,
  currency,
  onSubmit,
}: UseNewProposalFormParams) => {
  const {
    editorConfig,
    canUseRichTextEditor,
    loading: richTextEditorLoading,
  } = useRichTextEditor()
  const { data: competenciesData } = useQuery(
    TrainingRequestsCompetenciesDocument,
  )
  const competencies = competenciesData?.competencies?.competencies

  const competencyOptions = useMemo(
    () =>
      (competencies ?? []).map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    [competencies],
  )

  const schema = useMemo(
    () =>
      buildNewProposalSchema({
        allowEmployeePayment,
        canUseRichTextEditor,
        richTextEditorLoading,
        editorConfig,
        competencyOptions,
        currency,
      }),
    [
      allowEmployeePayment,
      canUseRichTextEditor,
      richTextEditorLoading,
      editorConfig,
      competencyOptions,
      currency,
    ],
  )

  const defaultValues = useMemo(
    () => ({
      trainingNeed: trainingNeedDefaults,
      basicInfo: basicInfoDefaults,
      timingCost: timingCostDefaults(allowEmployeePayment),
      employees: {
        employeesSelection: defaultEmployeeId
          ? {
              type: 'manual' as const,
              ids: [defaultEmployeeId],
            }
          : {
              type: 'manual' as const,
              ids: EMPTY_STRING_ARRAY,
            },
      },
      comments: commentsDefaults,
    }),
    [allowEmployeePayment, defaultEmployeeId],
  )

  const sections = useMemo(
    () => ({
      trainingNeed: trainingNeedSection(),
      basicInfo: basicInfoSection(),
      timingCost: timingCostSection(),
      employees: employeesSection(),
      comments: commentsSection(),
    }),
    [],
  )

  const steps: F0WizardFormStep[] = useMemo(() => {
    const base: F0WizardFormStep[] = [
      {
        title: trainingNeedSection().title,
        sectionIds: ['trainingNeed'],
      },
      {
        title: basicInfoSection().title,
        sectionIds: ['basicInfo'],
      },
      {
        title: timingCostSection().title,
        sectionIds: ['timingCost'],
      },
    ]

    if (showParticipantsStep) {
      base.push({
        title: employeesSection().title,
        sectionIds: ['employees'],
      })
    }

    base.push({
      title: commentsSection().title,
      nextLabel: i18n.t(
        'trainings.training_requests.create.send_for_approval',
      ),
      sectionIds: ['comments'],
    })

    return base
  }, [showParticipantsStep])

  const formDefinition = useF0FormDefinition({
    name: 'new-proposal-wizard',
    schema,
    sections,
    defaultValues,
    onSubmit,
  })

  return { formDefinition, steps }
}
